import { useState } from 'react';
import { useSiteContent } from '@/hooks/useSiteContent';
import ContentState from '@/components/feature/ContentState';

interface TripAdvisorWhyData {
  title: string;
  rating: string;
  platform: string;
  reviews: {
    name: string;
    location: string;
    avatar: string;
    rating: number;
    text: string;
    tour: string;
  }[];
}

export default function TripAdvisorWhy() {
  const [activeReview, setActiveReview] = useState(0);
  const { data, loading, error, refresh } = useSiteContent<TripAdvisorWhyData>(
    'why_us_tripadvisor',
  );

  if (loading || error || !data) {
    return <ContentState loading={loading} error={error} ready={!!data} onRetry={refresh} />;
  }

  return (
    <section className="w-full bg-background-200 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-foreground-600 mb-3">
              {data.title}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-500 mb-2">
              {data.rating}
            </h2>
            <p className="text-sm font-bold tracking-widest uppercase text-foreground-600">
              {data.platform}
            </p>
          </div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {data.reviews.map((review, idx) => (
              <div
                key={idx}
                className={`bg-background-50 border rounded-xl p-5 md:p-6 cursor-pointer transition-all ${
                  activeReview === idx
                    ? 'border-primary-400 shadow-md'
                    : 'border-background-200/70 hover:border-primary-300'
                }`}
                onClick={() => setActiveReview(idx)}
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-primary-700">
                      {review.avatar}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{review.name}</p>
                    <p className="text-xs text-foreground-500">{review.location}</p>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {Array.from({ length: review.rating }).map((_, rIdx) => (
                      <i key={rIdx} className="ri-star-fill text-accent-500 text-xs" />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                  &ldquo;{review.text}&rdquo;
                </p>
                <p className="text-xs text-primary-600 font-medium">{review.tour}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}